'use client';

import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { atomWithDefault } from 'jotai/utils';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { abTestBunja } from '@flipster/features/src/shared/ab-test';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { SymbolContext } from '@flipster/features/src/shared/symbol/bunja';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import CommunityFeed from '@web/app/[locale]/(web)/trade/_misc/etc-section/perpetual/community-feed';
import { feedNudgeBunja } from '@web/app/[locale]/(web)/trade/_misc/etc-section/perpetual/community-feed/bunja';
import YourPositions from '@web/app/[locale]/(web)/trade/_misc/etc-section/perpetual/your-postions';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import TradeHistory from './trade-history';
import { yourPositionsBunja } from './your-postions/state';

const PerpTabTypes = ['positions', 'social', 'history'] as const;
type PerpTabType = (typeof PerpTabTypes)[number];
const BaseTabType: PerpTabType = 'positions';

const Tabs = createTabsComponent<PerpTabType>();

export const perpEtcTabTypeBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { getAbFlagAtom } = bunja.use(abTestBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const mountedAtom = atom(false);
  const defaultWhileAtom = atom((get) => !get(mountedAtom));
  bunja.effect(() => store.set(mountedAtom, true));

  const isTradeHistoryEnabledAtom = getAbFlagAtom('web-trade-history-on-order-page', 'on', { defaultWhileAtom });

  const enabledTabsAtom = atom<PerpTabType[]>((get) => {
    const isLoggedIn = get(isCookieLoggedInAtom);
    const isTradeHistoryEnabled = get(isTradeHistoryEnabledAtom);

    if (!isLoggedIn || !isTradeHistoryEnabled) {
      return ['positions', 'social'];
    }
    return ['positions', 'social', 'history'];
  });

  const tabTypeAtom = atomWithDefault<PerpTabType>((get) => {
    if (typeof window === 'undefined') return BaseTabType;
    const searchParams = new URLSearchParams(window.location.search);
    const enabledTabs = get(enabledTabsAtom);
    const tabType = searchParams.get('tab');
    if (tabType && enabledTabs.includes(tabType as PerpTabType)) {
      return tabType as PerpTabType;
    }
    return BaseTabType;
  });

  return {
    tabTypeAtom,
    enabledTabsAtom,

    setTabType: (tabType: PerpTabType) => {
      store.set(tabTypeAtom, tabType);
      window.history.replaceState(null, '', `?tab=${tabType}`);
    },
  };
});
perpEtcTabTypeBunja.debugLabel = 'perpEtcTabTypeBunja';

export const PerpEtcSection = () => {
  const t = useTranslations();
  const tabRef = useRef<HTMLDivElement>(null);
  const isMounted = useMounted();

  useBunja(yourPositionsBunja);

  const { symbol } = useBunja(tradeSymbolBunja);
  const { tabTypeAtom, enabledTabsAtom, setTabType } = useBunja(perpEtcTabTypeBunja);
  const tabType = useAtomValue(tabTypeAtom);
  const { feedNudgeAtom } = useBunja(feedNudgeBunja);
  const feedNudge = useAtomValue(feedNudgeAtom);
  const tabs = useAtomValue(enabledTabsAtom);

  const isRedDotSocial = isMounted && feedNudge;
  const tabLabelMapper: Record<string, string> = {
    positions: t('Autogen_common.r251126.s_Trades'),
    social: t('Trade.r251104.Social'),
    history: t('Autogen_common.r251126.s_History'),
  };

  const onChangeTab = async (tabValue: PerpTabType) => {
    setTabType(tabValue);
    segment.track(CommunityEvent.OrderFormInfoTabClick, {
      symbol,
      trade_type: 'perpetual',
      tab_type: tabLabelMapper[tabValue],
    });
  };

  const onClickTab = (tabValue: PerpTabType) => {
    if (tabType === tabValue) tabRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className={css({
        gridArea: 'etc',
        display: { base: 'none', tablet: 'flex' },
        flexDir: 'column',
        h: '100%',
        bgColor: 'background.default.level-0',
        rounded: 'var(--grid-rounded)',
        minW: 0,
        overflow: 'hidden',
      })}
      ref={tabRef}
    >
      <Tabs
        value={tabType}
        type='scrollable'
        border
        dragHandle
        onChange={onChangeTab}
        onClick={onClickTab}
        panelCss={{ px: 16, pb: 12 }}
      >
        {tabs.map((tab) => (
          <Tabs.Tab key={tab} redDot={tab === 'social' && isRedDotSocial} label={tabLabelMapper[tab]} value={tab} />
        ))}
      </Tabs>
      <div
        className={flex({
          px: 16,
          pb: 12,
          flex: 1,
          minH: 0,
          overflow: 'hidden',
          flexDir: 'column',
        })}
      >
        {tabType === 'positions' && <YourPositions />}
        {tabType === 'social' && (
          <SymbolContext value={symbol}>
            <CommunityFeed />
          </SymbolContext>
        )}
        {tabType === 'history' && <TradeHistory />}
      </div>
    </section>
  );
};
